import { api } from './api'

const ENDPOINT = '/usage/events'
const FLUSH_INTERVAL_MS = 5000
const FLUSH_BATCH_SIZE = 20
const STORAGE_KEY = 'helpdesk_usage_queue'
const MAX_QUEUE = 500

type EventProps = Record<string, unknown>
interface QueuedEvent {
  event_type: string
  occurred_at: string
  page_path?: string
  page_name?: string
  properties: EventProps
}

let buffer: QueuedEvent[] = []
let timer: ReturnType<typeof setTimeout> | null = null
let lifecycleAttached = false

function detectDeviceType(): string {
  if (typeof navigator === 'undefined' || !navigator.userAgent) return 'unknown'
  const ua = navigator.userAgent
  if (/iPad|Android(?!.*Mobile)|Tablet|Silk(?!.*Mobile)/i.test(ua)) return 'tablet'
  if (/Mobi|iPhone|iPod|Android.*Mobile|Opera Mini|IEMobile/i.test(ua)) return 'mobile'
  if (/Macintosh/i.test(ua) && typeof navigator.maxTouchPoints === 'number' && navigator.maxTouchPoints > 1) {
    return 'tablet'
  }
  return 'desktop'
}

function parseBrowser(ua: string) {
  const match = (re: RegExp) => {
    const m = ua.match(re)
    return m ? Number.parseInt(m[1], 10) : undefined
  }
  if (/Edg\//i.test(ua)) return { browser: 'edge', browser_version: match(/Edg\/(\d+)/i) }
  if (/OPR\/|Opera\//i.test(ua)) return { browser: 'opera', browser_version: match(/(?:OPR|Opera)\/(\d+)/i) }
  if (/SamsungBrowser/i.test(ua)) return { browser: 'samsung', browser_version: match(/SamsungBrowser\/(\d+)/i) }
  if (/Firefox\/|FxiOS/i.test(ua)) return { browser: 'firefox', browser_version: match(/(?:Firefox|FxiOS)\/(\d+)/i) }
  if (/Chrome\/|CriOS/i.test(ua)) return { browser: 'chrome', browser_version: match(/(?:Chrome|CriOS)\/(\d+)/i) }
  if (/Safari\//i.test(ua)) return { browser: 'safari', browser_version: match(/Version\/(\d+)/i) }
  return { browser: 'other', browser_version: undefined }
}

function parseOs(ua: string): string {
  if (/Windows/i.test(ua)) return 'windows'
  if (/Android/i.test(ua)) return 'android'
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios'
  if (/CrOS/i.test(ua)) return 'chromeos'
  if (/Mac OS X|Macintosh/i.test(ua)) return 'macos'
  if (/Linux/i.test(ua)) return 'linux'
  return 'other'
}

function pageViewMetadata(): EventProps {
  const out: EventProps = { device_type: detectDeviceType() }
  if (typeof window !== 'undefined') {
    out.viewport_width = window.innerWidth
    out.viewport_height = window.innerHeight
    out.device_pixel_ratio = window.devicePixelRatio || 1
    if (window.screen) {
      out.screen_width = window.screen.width
      out.screen_height = window.screen.height
    }
  }
  if (typeof navigator !== 'undefined') {
    out.language = navigator.language || undefined
    const ua = navigator.userAgent || ''
    Object.assign(out, parseBrowser(ua))
    out.os = parseOs(ua)
  }
  try {
    out.timezone_offset_minutes = -new Date().getTimezoneOffset()
  } catch {
    /* noop */
  }
  return out
}

function readQueue(): QueuedEvent[] {
  try {
    return JSON.parse(window.localStorage.getItem(STORAGE_KEY) || '[]')
  } catch {
    return []
  }
}

function writeQueue(items: QueuedEvent[]) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items.slice(-MAX_QUEUE)))
  } catch {
    /* quota exceeded — drop silently */
  }
}

async function send(events: QueuedEvent[]) {
  if (!events.length) return
  try {
    await api(ENDPOINT, { method: 'POST', body: { events } })
  } catch {
    // Requeue for next flush so transient failures don't lose data.
    const queue = readQueue()
    writeQueue([...queue, ...events])
  }
}

function sendBeacon(events: QueuedEvent[]): boolean {
  if (!events.length || typeof navigator === 'undefined' || !navigator.sendBeacon) return false
  // Beacon cannot carry the Authorization header — best-effort only, the
  // visibilitychange path is what usually flushes successfully.
  const base = import.meta.env.VITE_API_URL || ''
  const blob = new Blob([JSON.stringify({ events })], { type: 'application/json' })
  return navigator.sendBeacon(`${base}${ENDPOINT}`, blob)
}

export async function flush({ beacon = false } = {}) {
  const persisted = readQueue()
  const all = [...persisted, ...buffer]
  if (!all.length) return
  buffer = []
  if (beacon) {
    const accepted = sendBeacon(all)
    if (accepted) writeQueue([])
    else writeQueue(all)
  } else {
    writeQueue([])
    await send(all)
  }
}

function scheduleFlush() {
  if (timer) return
  timer = setTimeout(async () => {
    timer = null
    await flush()
  }, FLUSH_INTERVAL_MS)
}

function attachLifecycleHandlers() {
  if (lifecycleAttached || typeof document === 'undefined') return
  lifecycleAttached = true
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush()
  })
  window.addEventListener('beforeunload', () => flush({ beacon: true }))
}

export function track(eventType: string, properties: EventProps = {}) {
  attachLifecycleHandlers()
  const enriched = eventType === 'page_view' ? { ...pageViewMetadata(), ...properties } : properties
  const event: QueuedEvent = { event_type: eventType, occurred_at: new Date().toISOString(), properties: enriched }
  if (typeof enriched.page_path === 'string') event.page_path = enriched.page_path as string
  if (typeof enriched.page_name === 'string') event.page_name = enriched.page_name as string
  buffer.push(event)
  if (buffer.length >= FLUSH_BATCH_SIZE) flush()
  else scheduleFlush()
}

let sessionStarted = false
export function ensureSessionStart() {
  if (sessionStarted) return
  sessionStarted = true
  track('session_start')
}

export function trackPageView(pagePath: string, extra: EventProps = {}) {
  track('page_view', { page_path: pagePath, ...extra })
}
